import { defineBlock, cssText, createRichText, isSafeUrl, resolveValue, isDarkColor, formatDate, type RichTextDoc, type InspectorSection, type VChild } from "@broadpaper/core";
import { checkMark, dotMark, triangleMark } from "../shared/marks.js";

const alignField: InspectorSection["fields"][number] = {
  kind: "segmented",
  prop: "align",
  label: "Align",
  options: [
    { value: "left", label: "Left", icon: "align-left" },
    { value: "center", label: "Centre", icon: "align-center" },
    { value: "right", label: "Right", icon: "align-right" }
  ]
};

/* ------------------------------------------------------------------ */
/* Heading                                                             */
/* ------------------------------------------------------------------ */

export const headingBlock = defineBlock<{ text: string; level: "h1" | "h2" | "h3" | "h4" }>({
  type: "heading",
  name: "Heading",
  description: "Section title using a document heading style",
  category: "content",
  icon: "heading",
  keywords: ["title", "h1", "h2", "h3"],
  defaultProps: { text: "Heading", level: "h2" },
  defaultTextStyle: "h2",
  defaultFlow: { keepWithNext: true },
  split: "none",
  styleGroups: ["textStyle", "typography", "alignment", "spacing", "flow"],
  inspector: [
    {
      title: "Heading",
      fields: [
        { kind: "text", prop: "text", label: "Text", placeholder: "Heading text or {{ token }}" },
        {
          kind: "segmented",
          prop: "level",
          label: "Level",
          options: [
            { value: "h1", label: "H1" },
            { value: "h2", label: "H2" },
            { value: "h3", label: "H3" },
            { value: "h4", label: "H4" }
          ]
        }
      ]
    }
  ],
  canvas: { inlineEdit: { prop: "text", kind: "text" }, label: (p) => p.text?.slice(0, 40) || "Heading" },
  render: ({ h, css, text, props, partAttrs, mode }) => {
    const content = text("text");
    const empty = content.length === 0 || (content.length === 1 && content[0] === "");
    return h(props.level || "h2", { class: ["bp-heading", `bp-heading--${props.level}`], style: cssText(css), ...partAttrs("text") }, empty && mode === "design" ? h("span", { class: "bp-placeholder-text" }, "Heading") : content);
  }
});

/* ------------------------------------------------------------------ */
/* Rich text                                                           */
/* ------------------------------------------------------------------ */

export const textBlock = defineBlock<{ content: RichTextDoc }>({
  type: "text",
  name: "Text",
  description: "Rich text with inline data tokens",
  category: "content",
  icon: "text",
  keywords: ["paragraph", "rich text", "body", "copy"],
  defaultProps: () => ({ content: createRichText("") }),
  defaultTextStyle: "body",
  split: "lines",
  measure: "text",
  styleGroups: ["textStyle", "typography", "alignment", "spacing", "background", "border", "flow"],
  inspector: [{ title: "Text", fields: [{ kind: "richtext", prop: "content", label: "Content" }] }],
  canvas: { inlineEdit: { prop: "content", kind: "richtext" }, label: () => "Text" },
  render: ({ h, css, richText, partAttrs, mode, node }) => {
    const doc = node.richText.content ?? (node.props.content as RichTextDoc | undefined);
    const isEmpty = !doc || doc.content.every((b) => b.type === "paragraph" && b.content.length === 0);
    return h("div", { class: ["bp-text", isEmpty && "bp-text--empty"], style: cssText(css), ...partAttrs("text") }, isEmpty && mode === "design" ? h("p", { class: "bp-p bp-placeholder-text" }, "Type or drop a field here…") : richText("content"));
  }
});

/* ------------------------------------------------------------------ */
/* Image & logo                                                        */
/* ------------------------------------------------------------------ */

export const imageBlock = defineBlock<{ src: string; alt: string; fit: "contain" | "cover"; width: number | null; height: number | null; align: "left" | "center" | "right"; radius: number; link: string }>({
  type: "image",
  name: "Image",
  description: "Static or data-bound image",
  category: "content",
  icon: "image",
  keywords: ["picture", "photo", "graphic"],
  defaultProps: { src: "", alt: "", fit: "contain", width: null, height: null, align: "left", radius: 0, link: "" },
  split: "none",
  styleGroups: ["spacing", "border", "flow"],
  inspector: [
    {
      title: "Image",
      fields: [
        { kind: "image", prop: "src", label: "Source", allowBinding: true },
        { kind: "text", prop: "alt", label: "Alt text", allowTokens: true },
        { kind: "text", prop: "link", label: "Link URL", allowTokens: true }
      ]
    },
    {
      title: "Size",
      fields: [
        { kind: "number", prop: "width", label: "Width", min: 8, max: 2000, unit: "px" },
        { kind: "number", prop: "height", label: "Height", min: 8, max: 2000, unit: "px" },
        {
          kind: "segmented",
          prop: "fit",
          label: "Fit",
          options: [
            { value: "contain", label: "Contain" },
            { value: "cover", label: "Cover" }
          ]
        },
        alignField,
        { kind: "number", prop: "radius", label: "Corner radius", min: 0, max: 200, unit: "px" }
      ]
    }
  ],
  validate: ({ props }) => {
    const src = String(props.src ?? "");
    if (src && !src.includes("{{") && !isSafeUrl(src, { allowData: true })) return [{ severity: "warning", code: "invalid-url", message: `Image URL "${src.slice(0, 60)}" is not an http(s) or data:image URL`, prop: "src" }];
    return [];
  },
  render: ({ h, css, props, mode, node, width: avail }) => {
    const src = typeof props.src === "string" ? props.src : "";
    const safe = src && isSafeUrl(src, { allowData: true });
    const w = props.width ?? undefined;
    const hgt = props.height ?? undefined;
    const justify = props.align === "center" ? "center" : props.align === "right" ? "flex-end" : "flex-start";
    const imgStyle: Record<string, string | undefined> = {
      width: w ? `${Math.min(w, avail)}px` : hgt ? "auto" : "100%",
      height: hgt ? `${hgt}px` : "auto",
      "max-width": "100%",
      "object-fit": props.fit,
      "border-radius": props.radius ? `${props.radius}px` : undefined,
      display: "block"
    };
    let img;
    if (safe) img = h("img", { src, alt: props.alt || "", style: imgStyle, draggable: "false" });
    else if (mode === "design") {
      const bound = node.bindings.src;
      img = h("div", { class: "bp-image-placeholder", style: { width: w ? `${Math.min(w, avail)}px` : "100%", height: `${hgt ?? 140}px`, "border-radius": props.radius ? `${props.radius}px` : undefined } }, h("span", { class: "bp-image-placeholder__label" }, bound ? bound.label : src ? "Invalid image URL" : "Choose an image"));
    } else img = h("div", { style: { width: w ? `${w}px` : "100%", height: `${hgt ?? 0}px` } });
    const linked = props.link && isSafeUrl(props.link) ? h("a", { href: props.link, rel: "noopener", style: { display: "block", width: w ? `${Math.min(w, avail)}px` : "100%" } }, img) : img;
    return h("div", { class: "bp-image", style: cssText({ ...css, display: "flex", "justify-content": justify }) }, linked);
  }
});

export const logoBlock = defineBlock<{ variant: "auto" | "light" | "dark"; height: number; align: "left" | "center" | "right" }>({
  type: "logo",
  name: "Logo",
  description: "The active brand's logo",
  category: "content",
  icon: "badge-check",
  keywords: ["brand", "theme"],
  defaultProps: { variant: "auto", height: 32, align: "left" },
  split: "none",
  styleGroups: ["spacing", "flow"],
  inspector: [
    {
      title: "Logo",
      fields: [
        { kind: "number", prop: "height", label: "Height", min: 8, max: 400, unit: "px" },
        alignField,
        {
          kind: "select",
          prop: "variant",
          label: "Variant",
          options: [
            { value: "auto", label: "Automatic" },
            { value: "light", label: "Standard" },
            { value: "dark", label: "For dark backgrounds" }
          ]
        }
      ]
    }
  ],
  render: ({ h, css, props, theme, mode, style }) => {
    const onDark = props.variant === "dark" || (props.variant === "auto" && !!style.background && isDarkColor(style.background));
    const src = (onDark && theme.logoDark) || theme.logo;
    const justify = props.align === "center" ? "center" : props.align === "right" ? "flex-end" : "flex-start";
    const content = src && isSafeUrl(src, { allowData: true })
      ? h("img", { src, alt: theme.logoAlt ?? `${theme.name} logo`, style: { height: `${props.height}px`, width: "auto", "max-width": "100%", display: "block" }, draggable: "false" })
      : mode === "design"
        ? h("div", { class: "bp-logo-placeholder", style: { height: `${props.height}px` } }, theme.name || "Logo")
        : h("div", { style: { height: `${props.height}px` } });
    return h("div", { class: "bp-logo", style: cssText({ ...css, display: "flex", "justify-content": justify }) }, content);
  }
});

/* ------------------------------------------------------------------ */
/* Divider, spacer                                                     */
/* ------------------------------------------------------------------ */

export const dividerBlock = defineBlock<{ thickness: number; color: string; lineStyle: "solid" | "dashed" | "dotted"; width: number }>({
  type: "divider",
  name: "Divider",
  category: "content",
  icon: "minus",
  keywords: ["rule", "line", "separator", "hr"],
  defaultProps: { thickness: 1, color: "$colors.border", lineStyle: "solid", width: 100 },
  split: "none",
  styleGroups: ["spacing", "flow"],
  inspector: [
    {
      title: "Divider",
      fields: [
        { kind: "number", prop: "thickness", label: "Thickness", min: 1, max: 20, unit: "px" },
        { kind: "color", prop: "color", label: "Colour" },
        {
          kind: "segmented",
          prop: "lineStyle",
          label: "Style",
          options: [
            { value: "solid", label: "Solid" },
            { value: "dashed", label: "Dashed" },
            { value: "dotted", label: "Dotted" }
          ]
        },
        { kind: "number", prop: "width", label: "Width", min: 5, max: 100, unit: "%" }
      ]
    }
  ],
  render: ({ h, css, props, theme }) =>
    h("div", { class: "bp-divider", style: cssText({ ...css, display: "flex", "justify-content": "center" }) }, h("div", { style: { width: `${props.width}%`, "border-top": `${props.thickness}px ${props.lineStyle} ${resolveValue(props.color, theme, theme.colors.border)}` } }))
});

export const spacerBlock = defineBlock<{ height: number }>({
  type: "spacer",
  name: "Spacer",
  category: "content",
  icon: "move-vertical",
  keywords: ["gap", "space", "padding"],
  defaultProps: { height: 24 },
  split: "none",
  styleGroups: ["flow"],
  inspector: [{ title: "Spacer", fields: [{ kind: "number", prop: "height", label: "Height", min: 1, max: 600, unit: "px" }] }],
  render: ({ h, props, mode }) => h("div", { class: ["bp-spacer", mode === "design" && "bp-spacer--design"], style: { height: `${props.height}px` } })
});

/* ------------------------------------------------------------------ */
/* Button / link                                                       */
/* ------------------------------------------------------------------ */

export const buttonBlock = defineBlock<{ label: string; url: string; variant: "filled" | "outline" | "link"; color: string; align: "left" | "center" | "right"; size: "sm" | "md" | "lg" }>({
  type: "button",
  name: "Button",
  description: "A hyperlink styled as a button",
  category: "content",
  icon: "mouse-pointer-click",
  keywords: ["link", "cta", "url"],
  defaultProps: { label: "View online", url: "https://", variant: "filled", color: "$colors.primary", align: "left", size: "md" },
  split: "none",
  styleGroups: ["typography", "spacing", "flow"],
  inspector: [
    {
      title: "Button",
      fields: [
        { kind: "text", prop: "label", label: "Label" },
        { kind: "text", prop: "url", label: "URL", placeholder: "https://… or {{ token }}" },
        {
          kind: "segmented",
          prop: "variant",
          label: "Style",
          options: [
            { value: "filled", label: "Filled" },
            { value: "outline", label: "Outline" },
            { value: "link", label: "Link" }
          ]
        },
        { kind: "color", prop: "color", label: "Colour" },
        {
          kind: "segmented",
          prop: "size",
          label: "Size",
          options: [
            { value: "sm", label: "S" },
            { value: "md", label: "M" },
            { value: "lg", label: "L" }
          ]
        },
        alignField
      ]
    }
  ],
  validate: ({ props }) => {
    const url = String(props.url ?? "");
    return url && !url.includes("{{") && !isSafeUrl(url) ? [{ severity: "warning", code: "invalid-url", message: `Button URL "${url}" is not a valid http(s) or mailto link`, prop: "url" }] : [];
  },
  render: ({ h, css, props, theme, text, style }) => {
    const color = resolveValue(props.color, theme, theme.colors.primary);
    const pad = props.size === "sm" ? "5px 10px" : props.size === "lg" ? "11px 22px" : "8px 16px";
    const fontSize = props.size === "sm" ? style.fontSize - 1 : props.size === "lg" ? style.fontSize + 2 : style.fontSize;
    const base: Record<string, string> = { display: "inline-block", padding: props.variant === "link" ? "0" : pad, "border-radius": `${theme.radius ?? 6}px`, "font-weight": "600", "font-size": `${fontSize}px`, "text-decoration": props.variant === "link" ? "underline" : "none", "font-family": style.fontFamily, "line-height": "1.2" };
    if (props.variant === "filled") Object.assign(base, { background: color, color: isDarkColor(color) ? "#FFFFFF" : theme.colors.text });
    else if (props.variant === "outline") Object.assign(base, { border: `1.5px solid ${color}`, color });
    else Object.assign(base, { color });
    const url = typeof props.url === "string" ? props.url : "";
    const justify = props.align === "center" ? "center" : props.align === "right" ? "flex-end" : "flex-start";
    return h("div", { class: "bp-button", style: cssText({ ...css, display: "flex", "justify-content": justify }) }, h("a", { class: "bp-button__link", href: isSafeUrl(url) ? url : undefined, rel: "noopener", style: base }, text("label", "Button")));
  }
});

/* ------------------------------------------------------------------ */
/* Callout & quote                                                     */
/* ------------------------------------------------------------------ */

export const calloutBlock = defineBlock<{ title: string; content: RichTextDoc; tone: "info" | "success" | "warning" | "danger" | "neutral"; icon: boolean }>({
  type: "callout",
  name: "Callout",
  description: "Highlighted note or warning",
  category: "content",
  icon: "message-square-warning",
  keywords: ["note", "warning", "alert", "notice", "info"],
  defaultProps: () => ({ title: "Important", content: createRichText("Add the note here."), tone: "info", icon: true }),
  defaultTextStyle: "body",
  defaultFlow: { keepTogether: true },
  split: "none",
  styleGroups: ["typography", "spacing", "flow"],
  inspector: [
    {
      title: "Callout",
      fields: [
        { kind: "text", prop: "title", label: "Title" },
        { kind: "richtext", prop: "content", label: "Content" },
        {
          kind: "select",
          prop: "tone",
          label: "Tone",
          options: [
            { value: "info", label: "Information" },
            { value: "success", label: "Success" },
            { value: "warning", label: "Warning" },
            { value: "danger", label: "Danger" },
            { value: "neutral", label: "Neutral" }
          ]
        },
        { kind: "toggle", prop: "icon", label: "Show icon" }
      ]
    }
  ],
  canvas: { inlineEdit: { prop: "content", kind: "richtext" }, label: (p) => p.title || "Callout" },
  render: ({ h, css, props, theme, text, richText, partAttrs }) => {
    const tones: Record<string, string> = { info: theme.colors.secondary, success: theme.colors.success ?? "#1E8E5A", warning: theme.colors.warning ?? "#C77D0A", danger: theme.colors.danger ?? "#C0392B", neutral: theme.colors.mutedText };
    const accent = tones[props.tone] ?? theme.colors.secondary;
    const glyphs: Record<string, string> = { info: "i", warning: "!", danger: "!" };
    const icon = glyphs[props.tone] ?? (props.tone === "success" ? checkMark(11, "#fff") : props.tone === "neutral" ? dotMark(11, "#fff") : "i");
    return h(
      "div",
      { class: ["bp-callout", `bp-callout--${props.tone}`], style: cssText({ ...css, display: "flex", gap: "12px", padding: css["padding"] ?? "12px 14px", "border-left": `3px solid ${accent}`, background: css["background"] ?? mixWithWhite(accent), "border-radius": `${theme.radius ?? 6}px` }) },
      props.icon ? h("div", { class: "bp-callout__icon", style: { color: "#fff", background: accent, "font-weight": "700", "font-size": "11px", width: "18px", height: "18px", "border-radius": "9px", display: "flex", "align-items": "center", "justify-content": "center", "flex-shrink": "0", "margin-top": "1px" } }, icon) : null,
      h("div", { class: "bp-callout__body", style: { flex: "1", "min-width": "0" } }, props.title ? h("div", { class: "bp-callout__title", style: { "font-weight": "600", "margin-bottom": "3px" } }, text("title")) : null, h("div", { class: "bp-callout__content", ...partAttrs("text") }, richText("content")))
    );
  }
});

export function mixWithWhite(hex: string, amount = 0.92): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return "transparent";
  const n = parseInt(m[1]!, 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(v + (255 - v) * amount));
  return `#${c.map((v) => v.toString(16).padStart(2, "0")).join("")}`;
}

export const quoteBlock = defineBlock<{ content: RichTextDoc; attribution: string }>({
  type: "quote",
  name: "Quote",
  category: "content",
  icon: "quote",
  keywords: ["blockquote", "testimonial", "citation"],
  defaultProps: () => ({ content: createRichText("A memorable statement."), attribution: "" }),
  defaultTextStyle: "lead",
  defaultStyle: { fontStyle: "italic" },
  defaultFlow: { keepTogether: true },
  split: "none",
  styleGroups: ["textStyle", "typography", "alignment", "spacing", "flow"],
  inspector: [
    {
      title: "Quote",
      fields: [
        { kind: "richtext", prop: "content", label: "Quote" },
        { kind: "text", prop: "attribution", label: "Attribution", placeholder: "— Name, role" }
      ]
    }
  ],
  canvas: { inlineEdit: { prop: "content", kind: "richtext" } },
  render: ({ h, css, props, theme, richText, text, partAttrs }) =>
    h(
      "blockquote",
      { class: "bp-quote", style: cssText({ ...css, margin: "0", "padding-left": "16px", "border-left": `3px solid ${theme.colors.accent}` }) },
      h("div", { class: "bp-quote__content", ...partAttrs("text") }, richText("content")),
      props.attribution ? h("div", { class: "bp-quote__attribution", style: { "font-style": "normal", "font-size": `${Math.max(8, Math.round(Number(css["font-size"]?.replace("px", "")) * 0.8))}px`, color: theme.colors.mutedText, "margin-top": "6px" } }, text("attribution")) : null
    )
});

/* ------------------------------------------------------------------ */
/* Key/value, KPI                                                      */
/* ------------------------------------------------------------------ */

export const fieldBlock = defineBlock<{ label: string; value: string; layout: "stacked" | "inline"; fallback: string; labelWidth: number }>({
  type: "field",
  name: "Field",
  description: "A labelled data value",
  category: "data",
  icon: "tag",
  keywords: ["key value", "label", "dynamic text", "binding", "data"],
  defaultProps: { label: "Label", value: "", layout: "stacked", fallback: "—", labelWidth: 40 },
  defaultTextStyle: "body",
  split: "none",
  styleGroups: ["typography", "alignment", "spacing", "flow"],
  inspector: [
    {
      title: "Field",
      fields: [
        { kind: "text", prop: "label", label: "Label" },
        { kind: "binding", prop: "value", label: "Value", placeholder: "Pick a field" },
        { kind: "text", prop: "fallback", label: "Fallback when empty", allowTokens: false },
        {
          kind: "segmented",
          prop: "layout",
          label: "Layout",
          options: [
            { value: "stacked", label: "Stacked" },
            { value: "inline", label: "Inline" }
          ]
        },
        { kind: "number", prop: "labelWidth", label: "Label width", min: 10, max: 80, unit: "%", when: (p) => p.layout === "inline" }
      ]
    }
  ],
  // A scalar field dropped on the page is a labelled value, with the formatter
  // its type implies already applied.
  fromField: ({ formatted, label }) => ({ label: label.split(" › ").pop() ?? label, value: formatted }),

  canvas: { label: (p) => p.label || "Field" },
  render: ({ h, css, props, theme, text, mode, node }) => {
    const b = node.bindings.value;
    const raw = props.value;
    const display = raw === undefined || raw === null || raw === "" ? props.fallback : String(raw);
    const value = mode === "design" && b ? h("span", { class: "bp-token", "data-expr": b.expr, title: b.expr }, b.label) : display;
    const label = h("div", { class: "bp-field__label", style: { color: theme.colors.mutedText, "font-size": "0.85em", "text-transform": "uppercase", "letter-spacing": "0.04em", "font-weight": "600" } }, text("label"));
    const val = h("div", { class: "bp-field__value", style: { "font-weight": "500" } }, value);
    if (props.layout === "inline") return h("div", { class: "bp-field bp-field--inline", style: cssText({ ...css, display: "flex", gap: "8px", "align-items": "baseline" }) }, h("div", { style: { flex: `0 0 ${props.labelWidth}%` } }, label), h("div", { style: { flex: "1", "min-width": "0" } }, val));
    return h("div", { class: "bp-field", style: cssText({ ...css, display: "flex", "flex-direction": "column", gap: "2px" }) }, label, val);
  }
});

export const kpiBlock = defineBlock<{ label: string; value: string; caption: string; trend: string; trendFormat: "percent" | "number" | "text"; accent: string; align: "left" | "center" | "right"; size: "sm" | "md" | "lg" }>({
  type: "kpi",
  name: "Statistic",
  description: "Big number with label and trend",
  category: "data",
  icon: "trending-up",
  keywords: ["kpi", "metric", "number", "stat", "big number"],
  defaultProps: { label: "Portfolio value", value: "", caption: "", trend: "", trendFormat: "percent", accent: "$colors.primary", align: "left", size: "md" },
  defaultTextStyle: "body",
  defaultFlow: { keepTogether: true },
  split: "none",
  styleGroups: ["typography", "spacing", "background", "border", "flow"],
  inspector: [
    {
      title: "Statistic",
      fields: [
        { kind: "text", prop: "label", label: "Label" },
        { kind: "binding", prop: "value", label: "Value", valueType: ["number", "currency", "percent", "string"] },
        { kind: "binding", prop: "trend", label: "Trend", valueType: ["number", "percent", "string"], placeholder: "e.g. portfolio.change" },
        {
          kind: "segmented",
          prop: "trendFormat",
          label: "Trend format",
          options: [
            { value: "percent", label: "%" },
            { value: "number", label: "#" },
            { value: "text", label: "Aa" }
          ]
        },
        { kind: "text", prop: "caption", label: "Caption" },
        { kind: "color", prop: "accent", label: "Accent" },
        alignField,
        {
          kind: "segmented",
          prop: "size",
          label: "Size",
          options: [
            { value: "sm", label: "S" },
            { value: "md", label: "M" },
            { value: "lg", label: "L" }
          ]
        }
      ]
    }
  ],
  canvas: { label: (p) => p.label || "Statistic" },
  render: ({ h, css, props, theme, text, mode, node, format }) => {
    const accent = resolveValue(props.accent, theme, theme.colors.primary);
    const size = props.size === "sm" ? 20 : props.size === "lg" ? 36 : 28;
    const b = node.bindings.value;
    const valueNode = h("span", { class: mode === "design" && b ? "bp-cell--bound" : undefined, title: b?.expr }, props.value === undefined || props.value === null || props.value === "" ? "—" : String(props.value));
    let trendNode = null;
    const tRaw = props.trend;
    if (tRaw !== undefined && tRaw !== null && tRaw !== "") {
      const n = Number(tRaw);
      const positive = Number.isFinite(n) ? n >= 0 : true;
      const label = props.trendFormat === "percent" && Number.isFinite(n) ? `${n > 0 ? "+" : ""}${new Intl.NumberFormat(format.locale, { style: "percent", maximumFractionDigits: 1 }).format(n)}` : props.trendFormat === "number" && Number.isFinite(n) ? `${n > 0 ? "+" : ""}${new Intl.NumberFormat(format.locale, { maximumFractionDigits: 2 }).format(n)}` : String(tRaw);
      const tb = node.bindings.trend;
      const trendColor = positive ? theme.colors.success ?? "#1E8E5A" : theme.colors.danger ?? "#C0392B";
      trendNode = h(
        "span",
        { class: ["bp-kpi__trend", positive ? "bp-kpi__trend--up" : "bp-kpi__trend--down", mode === "design" && tb ? "bp-cell--bound" : ""], title: tb?.expr, style: { color: trendColor, "font-weight": "600", "font-size": `${Math.round(size * 0.9)}px`, display: "inline-flex", "align-items": "center", gap: "4px", height: `${Math.round(size * 1.05)}px` } },
        triangleMark(positive ? "up" : "down", Math.max(9, Math.round(size * 0.55)), trendColor),
        h("span", null, label)
      );
    }
    return h(
      "div",
      { class: "bp-kpi", style: cssText({ ...css, "text-align": props.align, "border-top": css["border"] ? undefined : `3px solid ${accent}`, "padding-top": css["padding"] ? undefined : "8px" }) },
      h("div", { class: "bp-kpi__label", style: { color: theme.colors.mutedText, "font-size": "0.9em", "text-transform": "uppercase", "letter-spacing": "0.05em", "font-weight": "600", display: "flex", gap: "6px", "align-items": "center", "justify-content": props.align === "center" ? "center" : props.align === "right" ? "flex-end" : "flex-start", "flex-wrap": "wrap" } }, text("label"), mode === "design" && b ? h("span", { class: "bp-token", "data-expr": b.expr, style: { "text-transform": "none", "letter-spacing": "0", "font-weight": "500" } }, b.label) : null),
      h("div", { class: "bp-kpi__value", style: { "font-size": `${size}px`, "font-weight": "700", "line-height": "1.15", color: theme.colors.text, "font-family": theme.typography.headingFont, "letter-spacing": "-0.02em", "margin-top": "4px", display: "flex", "align-items": "baseline", gap: "8px", "justify-content": props.align === "center" ? "center" : props.align === "right" ? "flex-end" : "flex-start", "flex-wrap": "wrap" } }, valueNode, trendNode),
      props.caption ? h("div", { class: "bp-kpi__caption", style: { color: theme.colors.mutedText, "font-size": "0.9em", "margin-top": "3px" } }, text("caption")) : null
    );
  }
});

/* ------------------------------------------------------------------ */
/* Signature, date, page number, timestamp                             */
/* ------------------------------------------------------------------ */

export const signatureBlock = defineBlock<{ label: string; name: string; role: string; showDate: boolean; lineWidth: number }>({
  type: "signature",
  name: "Signature",
  description: "Signature line with name and date",
  category: "content",
  icon: "pen-line",
  keywords: ["sign", "approval"],
  defaultProps: { label: "Signed", name: "", role: "", showDate: true, lineWidth: 240 },
  defaultTextStyle: "body",
  defaultFlow: { keepTogether: true },
  split: "none",
  styleGroups: ["typography", "spacing", "flow"],
  inspector: [
    {
      title: "Signature",
      fields: [
        { kind: "text", prop: "label", label: "Label" },
        { kind: "text", prop: "name", label: "Name", placeholder: "{{ adviser.name }}" },
        { kind: "text", prop: "role", label: "Role" },
        { kind: "toggle", prop: "showDate", label: "Date line" },
        { kind: "number", prop: "lineWidth", label: "Line width", min: 80, max: 600, unit: "px" }
      ]
    }
  ],
  render: ({ h, css, props, theme, text }) => {
    const line = (label: string, content: VChild[]) =>
      h("div", { style: { width: `${props.lineWidth}px`, "max-width": "100%" } }, h("div", { style: { height: "36px", "border-bottom": `1px solid ${theme.colors.text}` } }), h("div", { style: { "font-size": "0.85em", color: theme.colors.mutedText, "margin-top": "4px" } }, label), content.length ? h("div", { style: { "font-weight": "600", "margin-top": "2px" } }, content) : null);
    return h("div", { class: "bp-signature", style: cssText({ ...css, display: "flex", gap: "32px", "flex-wrap": "wrap" }) }, line(props.label || "Signed", [...text("name"), ...(props.role ? [h("div", { style: { "font-weight": "400", color: theme.colors.mutedText } }, text("role"))] : [])]), props.showDate ? line("Date", []) : null);
  }
});

export const dateBlock = defineBlock<{ source: "now" | "binding"; value: string; pattern: string; prefix: string }>({
  type: "date",
  name: "Date",
  description: "Today's date or a bound date field",
  category: "content",
  icon: "calendar",
  keywords: ["today", "generated", "date"],
  defaultProps: { source: "now", value: "", pattern: "d MMMM yyyy", prefix: "" },
  defaultTextStyle: "body",
  split: "none",
  styleGroups: ["textStyle", "typography", "alignment", "spacing", "flow"],
  inspector: [
    {
      title: "Date",
      fields: [
        {
          kind: "segmented",
          prop: "source",
          label: "Source",
          options: [
            { value: "now", label: "Today" },
            { value: "binding", label: "Field" }
          ]
        },
        { kind: "binding", prop: "value", label: "Date field", valueType: ["date", "datetime"], when: (p) => p.source === "binding" },
        { kind: "text", prop: "pattern", label: "Format", placeholder: "d MMMM yyyy", allowTokens: false },
        { kind: "text", prop: "prefix", label: "Prefix", placeholder: "Prepared on " }
      ]
    }
  ],
  render: ({ h, css, props, format, text, mode, node }) => {
    const d = props.source === "now" ? format.now ?? new Date() : props.value;
    const b = node.bindings.value;
    const body = mode === "design" && props.source === "binding" && b ? h("span", { class: "bp-token", "data-expr": b.expr }, b.label) : formatDate(d, props.pattern || undefined, format);
    return h("div", { class: "bp-date", style: cssText(css) }, text("prefix"), body);
  }
});

export const pageNumberBlock = defineBlock<{ format: string; align: "left" | "center" | "right" }>({
  type: "pageNumber",
  name: "Page number",
  category: "content",
  icon: "hash",
  keywords: ["page", "of", "footer", "numbering"],
  defaultProps: { format: "Page {{page.number}} of {{page.total}}", align: "right" },
  defaultTextStyle: "caption",
  split: "none",
  styleGroups: ["textStyle", "typography", "spacing"],
  inspector: [
    {
      title: "Page number",
      fields: [
        { kind: "text", prop: "format", label: "Format", placeholder: "Page {{page.number}} of {{page.total}}" },
        alignField
      ]
    }
  ],
  render: ({ h, css, props, text }) => h("div", { class: "bp-page-number", style: cssText({ ...css, "text-align": props.align }) }, text("format"))
});

export const timestampBlock = defineBlock<{ prefix: string; pattern: string; align: "left" | "center" | "right" }>({
  type: "timestamp",
  name: "Generated at",
  description: "Date and time the document was generated",
  category: "content",
  icon: "clock",
  keywords: ["generated", "printed", "timestamp", "time"],
  defaultProps: { prefix: "Generated ", pattern: "d MMM yyyy, HH:mm", align: "left" },
  defaultTextStyle: "caption",
  split: "none",
  styleGroups: ["textStyle", "typography", "spacing"],
  inspector: [
    {
      title: "Generated at",
      fields: [
        { kind: "text", prop: "prefix", label: "Prefix" },
        { kind: "text", prop: "pattern", label: "Format", allowTokens: false },
        alignField
      ]
    }
  ],
  render: ({ h, css, props, format, text }) => h("div", { class: "bp-timestamp", style: cssText({ ...css, "text-align": props.align }) }, text("prefix"), formatDate(format.now ?? new Date(), props.pattern || undefined, format, true))
});
